import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {aliasToCSV} from 'molbio/monomers/custom-syntax/AliasCreationForm';
import AliasEditModal from 'molbio/monomers/custom-syntax/AliasEditModal';
import {AliasData, deleteCustomSyntaxAlias} from 'molbio/monomers/MonomersDAO';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';
import {Cell, Table, Column} from 'ui-library/simple-table/SimpleTable';
import copyToClipboard from 'util/clipboard/copyToClipboard';

function aliasesToCSV(aliases: Array<AliasData>): string {
  return aliases.map((alias) => aliasToCSV(alias)).join('\n');
}

export function updateAliases(
  updatedAlias: AliasData,
  aliases: Array<AliasData>,
  isDelete: boolean
): Array<AliasData> {
  if (isDelete) {
    return aliases.filter((a) => a.id !== updatedAlias.id);
  }
  return aliases.map((a) => (a.id === updatedAlias.id ? updatedAlias : a));
}

export function DeleteAliasConfirmationModal({
  alias,
  onRequestSubmit,
  onRequestClose,
  deleteAlias,
}: {
  alias: AliasData;
  onRequestSubmit: (deletedId: number) => void;
  onRequestClose: () => void;
  deleteAlias: () => Promise<void>;
}): JSX.Element {
  const submit = async (): Promise<void> => {
    await deleteAlias();
    onRequestSubmit(alias.id);
  };

  return (
    <Modal
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        submit();
      }}
      buttons={[
        {
          style: ButtonStyle.SECONDARY,
          submit: true,
          text: 'Yes, delete alias',
        },
      ]}
      title={`Delete alias '${alias.token}'?`}
    >
      <p>
        Deleting this alias may affect the ability to import/export data with this custom notation. Any
        computed fields that use this custom notation may also be affected.
      </p>
    </Modal>
  );
}

export default function AliasTable({
  aliases,
  syntaxName,
  updateLocalState,
}: {
  aliases: Array<AliasData>;
  syntaxName: string;
  updateLocalState: (alias: AliasData, isDelete: boolean) => void;
}): JSX.Element {
  return (
    <Table numRows={aliases.length} frozenHeader={true} overflowVisible={true} className={css(styles.table)}>
      <Column
        header={
          <Cell className={classNames(css(styles.tableHeader), css(styles.td))}>{syntaxName} token</Cell>
        }
        renderCell={(rowIndex: number) => <Cell className={css(styles.td)}>{aliases[rowIndex].token}</Cell>}
      />
      <Column
        header={
          <Cell className={classNames(css(styles.tableHeader), css(styles.td))}>Equivalent HELM token</Cell>
        }
        renderCell={(rowIndex: number) => (
          <Cell className={css(styles.td)}>{aliases[rowIndex].helmToken}</Cell>
        )}
      />
      <Column
        header={<Cell className={classNames(css(styles.tableHeader), css(styles.td))}>5' variant</Cell>}
        renderCell={(rowIndex: number) => (
          <Cell className={css(styles.td)}>{aliases[rowIndex].tokenVariantStart ?? ''}</Cell>
        )}
      />
      <Column
        header={<Cell className={classNames(css(styles.tableHeader), css(styles.td))}>3' variant</Cell>}
        renderCell={(rowIndex: number) => (
          <Cell className={css(styles.td)}>{aliases[rowIndex].tokenVariantEnd ?? ''}</Cell>
        )}
      />
      <Column
        header={
          <Cell className={classNames(css(styles.td))}>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              icon="copy"
              size={ButtonSize.MINI}
              onClick={() => {
                copyToClipboard(aliasesToCSV(aliases), {showToastOnSuccess: true});
              }}
              text="Copy all"
              disabled={aliases.length === 0}
            />
          </Cell>
        }
        renderCell={(rowIndex: number) => (
          <Cell className={css({...styles.td, maxWidth: 400})}>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              icon="copy"
              className="u-mini-spacing-right"
              size={ButtonSize.MINI}
              onClick={() => {
                copyToClipboard(aliasToCSV(aliases[rowIndex]), {showToastOnSuccess: true});
              }}
            />
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              icon="edit"
              className="u-mini-spacing-right"
              size={ButtonSize.MINI}
              onClick={async () => {
                const updatedAlias = await runModal(AliasEditModal, {initialState: aliases[rowIndex]});
                if (updatedAlias) {
                  updateLocalState(updatedAlias, false);
                }
              }}
            />
            <Button
              buttonStyle={ButtonStyle.NEGATIVE}
              icon="delete"
              size={ButtonSize.MINI}
              onClick={async () => {
                const deletedId = await runModal(DeleteAliasConfirmationModal, {
                  alias: aliases[rowIndex],
                  deleteAlias: async () => {
                    await deleteCustomSyntaxAlias(aliases[rowIndex].syntaxId, aliases[rowIndex].id);
                  },
                });
                if (deletedId != null) {
                  updateLocalState(aliases[rowIndex], true);
                }
              }}
            />
          </Cell>
        )}
      />
    </Table>
  );
}

const styles = StyleSheet.create({
  td: {
    padding: '10px',
    maxWidth: '300px',
    overflowWrap: 'break-word',
  },
  tableRow: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
  },
  tableHeader: {
    fontWeight: 'bold',
  },
  table: {
    overflow: 'auto',
    height: '300px',
  },
});
